import { env } from '@/lib/env'
import { ApiError, NetworkError, type ApiDetail } from './errors'

export interface AuthHeaders {
  /** Sprint JWT (club or admin) for Authorization: Bearer. */
  accessToken?: string | null
  /** EmJogo provider session for X-Provider-Session. */
  providerSession?: string | null
  /** Score session for X-Score-Session. */
  scoreSession?: string | null
}

export interface RequestOptions<Body = unknown> {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE'
  body?: Body
  query?: Record<string, string | number | boolean | null | undefined>
  /** When true, skip Content-Type and send body as-is (used for multipart). */
  raw?: boolean
  signal?: AbortSignal
  headers?: Record<string, string>
  auth?: AuthHeaders
}

function buildUrl(
  path: string,
  query?: RequestOptions['query'],
): string {
  const base = env.apiUrl.replace(/\/+$/, '')
  const suffix = path.startsWith('/') ? path : `/${path}`
  const url = new URL(base + suffix)
  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value === null || value === undefined) continue
      url.searchParams.set(key, String(value))
    }
  }
  return url.toString()
}

function buildHeaders(options: RequestOptions): Headers {
  const headers = new Headers(options.headers ?? {})
  if (options.body !== undefined && !options.raw) {
    if (!headers.has('Content-Type')) {
      headers.set('Content-Type', 'application/json')
    }
  }
  if (!headers.has('Accept')) headers.set('Accept', 'application/json')
  const auth = options.auth
  if (auth?.accessToken) {
    headers.set('Authorization', `Bearer ${auth.accessToken}`)
  }
  if (auth?.providerSession) {
    headers.set('X-Provider-Session', auth.providerSession)
  }
  if (auth?.scoreSession) {
    headers.set('X-Score-Session', auth.scoreSession)
  }
  return headers
}

async function parseErrorBody(response: Response): Promise<{
  detail: ApiDetail
  body: unknown
}> {
  const contentType = response.headers.get('Content-Type') ?? ''
  if (contentType.includes('application/json')) {
    try {
      const body = (await response.json()) as Record<string, unknown>
      const detail = (body && (body as { detail?: ApiDetail }).detail) as ApiDetail
      return { detail, body }
    } catch {
      return { detail: undefined, body: null }
    }
  }
  try {
    const text = await response.text()
    return { detail: text || undefined, body: text }
  } catch {
    return { detail: undefined, body: null }
  }
}

function describeError(detail: ApiDetail, status: number, url: string): string {
  if (typeof detail === 'string' && detail) return detail
  if (Array.isArray(detail) && detail[0] && typeof detail[0] === 'object') {
    const first = detail[0] as { msg?: string; loc?: Array<string | number> }
    return first.msg ? `Validation error: ${first.msg}` : `Validation error (${status})`
  }
  if (detail && typeof detail === 'object' && 'message' in detail) {
    const message = (detail as { message?: unknown }).message
    if (typeof message === 'string') return message
  }
  return `HTTP ${status} at ${url}`
}

/**
 * Low-level JSON-by-default HTTP call. Normalizes errors into {@link ApiError}
 * and {@link NetworkError}. Callers generally use the typed endpoint helpers.
 */
export async function request<Response = unknown, Body = unknown>(
  path: string,
  options: RequestOptions<Body> = {},
): Promise<Response> {
  const url = buildUrl(path, options.query)
  const headers = buildHeaders(options)
  const method = options.method ?? 'GET'
  const init: RequestInit = {
    method,
    headers,
    signal: options.signal,
  }
  if (options.body !== undefined) {
    init.body = options.raw
      ? (options.body as BodyInit)
      : JSON.stringify(options.body)
  }

  let response: Response
  try {
    response = (await fetch(url, init)) as unknown as Response
  } catch (cause) {
    throw new NetworkError(url, cause)
  }
  const httpResponse = response as unknown as {
    ok: boolean
    status: number
    headers: Headers
    json: () => Promise<unknown>
    text: () => Promise<string>
  }

  if (!httpResponse.ok) {
    const { detail, body } = await parseErrorBody(httpResponse as unknown as globalThis.Response)
    throw new ApiError(describeError(detail, httpResponse.status, url), {
      status: httpResponse.status,
      detail,
      body,
      url,
    })
  }
  if (httpResponse.status === 204) return undefined as Response

  const contentType = httpResponse.headers.get('Content-Type') ?? ''
  if (contentType.includes('application/json')) {
    return (await httpResponse.json()) as Response
  }
  return (await httpResponse.text()) as unknown as Response
}

/**
 * Fetch a raw (non-JSON) response. Used for the match-sheet PDF report
 * endpoint which returns application/pdf. The caller receives the Response
 * object to decide how to handle it (e.g. open in a blob URL).
 */
export async function requestRaw(
  path: string,
  options: RequestOptions = {},
): Promise<globalThis.Response> {
  const url = buildUrl(path, options.query)
  const headers = buildHeaders(options)
  const method = options.method ?? 'GET'
  let response: globalThis.Response
  try {
    response = await fetch(url, {
      method,
      headers,
      signal: options.signal,
      body:
        options.body !== undefined
          ? options.raw
            ? (options.body as BodyInit)
            : JSON.stringify(options.body)
          : undefined,
    })
  } catch (cause) {
    throw new NetworkError(url, cause)
  }
  if (!response.ok) {
    const { detail, body } = await parseErrorBody(response)
    throw new ApiError(describeError(detail, response.status, url), {
      status: response.status,
      detail,
      body,
      url,
    })
  }
  return response
}
